import {
  extractSignerTemplateMarkersFromPdf,
  findSignerTemplateMarkerForField,
  getSignerTemplateMarkerDetails,
  getSignerTemplateMarkerTextStyle,
} from "@/components/features/matter/file-repo/signature-requests/signatureRequestTemplateFields";
import type { LayoutField } from "@/components/features/matter/file-repo/signature-requests/SignatureRequestPdfLayoutEditor.constants";
import { useEffect, type Dispatch, type SetStateAction } from "react";

export function useSignerTemplateFields<T extends LayoutField>(
  sourceUrl: string | undefined,
  setFields: Dispatch<SetStateAction<T[]>>,
) {
  useEffect(() => {
    if (!sourceUrl) return;
    const controller = new AbortController();
    void (async () => {
      const response = await fetch(sourceUrl, { signal: controller.signal });
      if (!response.ok) throw new Error("Unable to read signer template");
      const markers = await extractSignerTemplateMarkersFromPdf(
        await response.arrayBuffer(),
      );
      if (controller.signal.aborted) return;
      setFields((current) =>
        current.map((field) => {
          if (field.type !== "date" && field.type !== "text") return field;
          const marker = findSignerTemplateMarkerForField(field, markers);
          if (!marker) return field;
          const style = getSignerTemplateMarkerTextStyle(marker);
          return {
            ...field,
            fontFamily: field.fontFamily ?? style.fontFamily,
            fontSize: field.fontSize ?? style.fontSize,
            label:
              field.type === "date" &&
              field.label === "Date" &&
              getSignerTemplateMarkerDetails(marker.variable)?.fieldLabel ===
                "Signing Date"
                ? "Signing Date"
                : field.label,
          };
        }),
      );
    })().catch(() => {
      // Ambiguous legacy fields stay independent when their source cannot be read.
    });
    return () => {
      controller.abort();
    };
  }, [sourceUrl, setFields]);
}
